/**
 * Application Footer Component
 * 
 * Displays version information, beta status, and links.
 * Automatically shows/hides based on build configuration.
 */

import React from 'react';
import { Badge } from './badge';
import { Button } from './button';
import { ExternalLink, Info } from 'lucide-react';

// Import version information (will be created by build script)
let versionInfo: any = {};
try {
  const version = require('@/app/version');
  versionInfo = {
    version: version.APP_VERSION || '0.0.0',
    buildNumber: version.BUILD_NUMBER || 'dev',
    isBeta: version.IS_BETA || false,
    buildDate: version.BUILD_DATE || new Date().toISOString(),
    environment: version.ENVIRONMENT || 'development'
  };
} catch (error) {
  // Fallback for development when version file doesn't exist
  versionInfo = {
    version: import.meta.env.VITE_APP_VERSION || '0.0.0-dev',
    buildNumber: import.meta.env.VITE_BUILD_NUMBER || 'dev',
    isBeta: import.meta.env.VITE_BETA === 'true',
    buildDate: import.meta.env.VITE_BUILD_DATE || new Date().toISOString(),
    environment: import.meta.env.MODE || 'development'
  };
}

export interface AppFooterProps {
  /** Whether to show the footer (defaults to true for beta builds) */
  show?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Compact mode (smaller footer) */
  compact?: boolean;
}

export function AppFooter({ 
  show = versionInfo.isBeta || versionInfo.environment === 'development',
  className = '',
  compact = false 
}: AppFooterProps) {
  
  if (!show) {
    return null;
  }

  const formatBuildDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Unknown';
    }
  };

  const getBetaBadgeVariant = () => {
    if (versionInfo.isBeta) return 'secondary';
    if (versionInfo.environment === 'development') return 'outline';
    return 'default';
  };

  const getBetaBadgeText = () => {
    if (versionInfo.isBeta) return 'BETA';
    if (versionInfo.environment === 'development') return 'DEV';
    return 'PROD';
  };

  return (
    <footer className={`bg-muted/30 border-t border-border/40 ${className}`}>
      <div className={`max-w-7xl mx-auto px-4 ${compact ? 'py-2' : 'py-4'}`}>
        <div className="flex items-center justify-between">
          
          {/* Left: Version Information */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Badge variant={getBetaBadgeVariant()} className="text-xs">
                {getBetaBadgeText()}
              </Badge>
              <span className="text-sm font-medium text-foreground">
                v{versionInfo.version}
              </span>
            </div>
            
            {!compact && (
              <div className="hidden sm:flex items-center gap-2 text-xs text-muted-foreground">
                <span>Build {versionInfo.buildNumber}</span>
                <span>•</span>
                <span>{formatBuildDate(versionInfo.buildDate)}</span>
              </div>
            )}
          </div>

          {/* Right: Links and Actions */}
          <div className="flex items-center gap-2">
            
            {/* Beta Feedback Link */}
            {versionInfo.isBeta && (
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-7"
                onClick={() => {
                  // This will be handled by the feedback modal hook
                  const event = new CustomEvent('open-feedback-modal', {
                    detail: { source: 'footer' }
                  });
                  window.dispatchEvent(event);
                }}
              >
                <Info className="h-3 w-3 mr-1" />
                Feedback
              </Button>
            )}

            {/* Documentation Link */}
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-7"
              asChild
            >
              <a 
                href="https://docs.nodoublesgolf.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center"
              >
                <ExternalLink className="h-3 w-3 mr-1" />
                Docs
              </a>
            </Button>

            {/* Version Details (Development Only) */}
            {versionInfo.environment === 'development' && (
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-7"
                onClick={() => {
                  console.log('🔍 Version Info:', versionInfo);
                  console.log('🚩 Feature Flags:', (window as any).__APP_VERSION__?.featureFlags);
                }}
              >
                Debug
              </Button>
            )}
          </div>
        </div>

        {/* Beta Notice (Compact Mode) */}
        {versionInfo.isBeta && !compact && (
          <div className="mt-2 pt-2 border-t border-border/20">
            <p className="text-xs text-muted-foreground">
              🧪 You're using a beta version. Your feedback helps us improve!{' '}
              <button 
                className="underline hover:no-underline"
                onClick={() => {
                  const event = new CustomEvent('open-feedback-modal', {
                    detail: { source: 'beta_notice' }
                  });
                  window.dispatchEvent(event);
                }}
              >
                Report issues here
              </button>
            </p>
          </div>
        )}
      </div>
    </footer>
  );
}

/**
 * Hook to get version information for use in other components
 */
export function useVersionInfo() {
  return versionInfo;
}

/**
 * Get version info for inclusion in API requests or feedback
 */
export function getVersionForPayload() {
  return {
    app_version: versionInfo.version,
    build_number: versionInfo.buildNumber,
    build_date: versionInfo.buildDate,
    environment: versionInfo.environment,
    is_beta: versionInfo.isBeta
  };
}

export default AppFooter;
